import React, { useState } from 'react'
import DashModalV2 from '../../../components/DashModalV2'
import { AiOutlineSearch } from "react-icons/ai"
import { Btn, COLORS, Chip, Drawer, EmptyState, Field, INPUT_STYLE, Kpi, Stepper, addMonths, downloadCsv, fmtStamp, money, monthKey, monthLabelOf, pct, todayIso, useNarrow, useSaving } from '../FieldOpsKit'
import { goalOf, routesOf, salesOf } from '../store'
import ApprovalBar from './ApprovalBar'

/**
 * Goals (editors): one monthly goal per store. Editable only while the month is a Draft. Tools: copy the previous
 * month, apply a % change, paste from Excel, export, and a change history. Totals update as you type.
 */
const STATUS_LABEL = { draft: 'Draft', pending: 'Pending approval', published: 'Published' }

export default function GoalsTab({ ctx }) {
    const sales = ctx.state.sales
    const cur = monthKey(todayIso())
    const [month, setMonth] = useState(addMonths(cur, 1)) // the month being prepared
    const [search, setSearch] = useState('')
    const [route, setRoute] = useState('all')
    const [onlyMissing, setOnlyMissing] = useState(false)
    const [modal, setModal] = useState(null) // 'percent' | 'paste'
    const [history, setHistory] = useState(false)
    const narrow = useNarrow(900)

    const period = sales.periods[month]
    const prev = addMonths(month, -1)
    const editable = !!period && period.status === 'draft' && ctx.can.editGoals
    const why = !period ? '' : period.status !== 'draft' ? 'Goals are locked while the month is ' + STATUS_LABEL[period.status].toLowerCase() + '.' : !ctx.can.editGoals ? 'Your demo user cannot edit goals (only editors and the super admin).' : ''

    const q = search.trim().toLowerCase()
    const all = sales.stores.map((s) => ({ store: s, goal: goalOf(sales, month, s.id, false), prevGoal: goalOf(sales, prev, s.id, false), prevSales: salesOf(sales, prev, s.id) }))
    const shown = all.filter((r) => {
        if (route !== 'all' && r.store.route !== route) return false
        if (onlyMissing && r.goal !== null) return false
        if (q === '') return true
        return r.store.name.toLowerCase().indexOf(q) >= 0 || r.store.city.toLowerCase().indexOf(q) >= 0
    })
    const total = all.reduce((a, r) => a + (r.goal || 0), 0)
    const prevTotal = all.reduce((a, r) => a + (r.prevGoal || 0), 0)
    const missing = all.filter((r) => r.goal === null).length
    const routeNames = routesOf(sales)

    const setGoal = (storeId, raw) => {
        if (!editable) return
        if (raw === '') { ctx.dispatch({ type: 'GOAL_SET', month: month, storeId: storeId, amount: null, by: ctx.me.name }); return }
        const n = Math.max(0, Math.round(Number(raw)))
        if (isNaN(n)) return
        ctx.dispatch({ type: 'GOAL_SET', month: month, storeId: storeId, amount: n, by: ctx.me.name })
    }
    const copyPrev = () => {
        const entries = {}
        all.forEach((r) => { if (r.prevGoal !== null) entries[r.store.id] = r.prevGoal })
        ctx.dispatch({ type: 'GOALS_BULK', month: month, entries: entries, by: ctx.me.name, note: 'Copied from ' + monthLabelOf(prev) })
        ctx.toast('Copied ' + Object.keys(entries).length + ' goals from ' + monthLabelOf(prev) + '.')
    }
    const exportCsv = () => {
        const out = [['Month', 'Status', 'Route', 'Store', 'City', 'Salesperson', 'Goal', 'Previous goal', 'Previous sales']]
        all.forEach((r) => out.push([month, period ? period.status : '', r.store.route, r.store.name, r.store.city, r.store.rep, r.goal === null ? '' : r.goal, r.prevGoal === null ? '' : r.prevGoal, Math.round(r.prevSales)]))
        downloadCsv('goals-' + month + '.csv', out)
        ctx.toast('Goals exported to CSV.')
    }

    const input = (r) => (
        <input type="number" min="0" step="250" disabled={!editable} value={r.goal === null ? '' : r.goal} placeholder="—"
            onChange={(e) => setGoal(r.store.id, e.target.value)} title={why}
            style={{ ...INPUT_STYLE, width: 120, padding: '0.25rem 0.5rem', borderColor: r.goal === null ? COLORS.amber : COLORS.line, background: editable ? '#FFFFFF' : COLORS.soft }} aria-label={'Goal for ' + r.store.name} />
    )
    const change = (r) => (r.goal !== null && r.prevGoal ? (r.goal >= r.prevGoal ? '+' : '') + Math.round((r.goal / r.prevGoal - 1) * 100) + '%' : '—')

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <Stepper dark label={monthLabelOf(month)} onPrev={() => setMonth(addMonths(month, -1))} onNext={() => setMonth(addMonths(month, 1))} canPrev={month > addMonths(cur, -2)} canNext={month < addMonths(cur, 1)} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Btn kind="secondary" disabled={!editable} title={why} onClick={copyPrev}>Copy {monthLabelOf(prev).split(' ')[0]}</Btn>
                    <Btn kind="secondary" disabled={!editable} title={why} onClick={() => setModal('percent')}>Apply % change</Btn>
                    <Btn kind="secondary" disabled={!editable} title={why} onClick={() => setModal('paste')}>Paste from Excel</Btn>
                    <Btn kind="secondary" onClick={() => setHistory(true)}>History</Btn>
                    <Btn kind="secondary" onClick={exportCsv}>Export CSV</Btn>
                </div>
            </div>

            <ApprovalBar ctx={ctx} month={month} />
            {why ? <div style={{ fontSize: '0.78rem', color: COLORS.slate, marginBottom: 10 }}>{why}</div> : null}

            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3"><Kpi label="Total goal" value={money(total)} sub={prevTotal ? (total >= prevTotal ? '+' : '') + Math.round((total / prevTotal - 1) * 100) + '% vs ' + monthLabelOf(prev).split(' ')[0] : ''} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Stores with goal" value={(all.length - missing) + ' / ' + all.length} sub={pct(all.length - missing, all.length) + '% complete'} status={missing === 0 ? 'success' : 'warning'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Without goal" value={missing} sub={missing === 0 ? 'Ready to submit' : 'Fill them in or submit anyway'} status={missing === 0 ? 'success' : 'danger'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Average per store" value={money(all.length - missing > 0 ? total / (all.length - missing) : 0)} sub={'Previous ' + money(prevTotal / Math.max(1, all.filter((r) => r.prevGoal !== null).length))} /></div>
            </div>

            <div className="row g-2 mb-3 align-items-center">
                <div className="col-12 col-md-5"><div style={{ position: 'relative' }}>
                    <AiOutlineSearch style={{ position: 'absolute', left: 12, top: 11, color: COLORS.muted }} />
                    <input type="text" placeholder="Search store or city" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...INPUT_STYLE, width: '100%', paddingLeft: 34 }} />
                </div></div>
                <div className="col-6 col-md-3">
                    <select value={route} onChange={(e) => setRoute(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }} aria-label="Route">
                        <option value="all">All routes</option>
                        {routeNames.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-4"><label style={{ fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer' }}>
                    <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Only stores without goal
                </label></div>
            </div>

            {shown.length === 0 ? <EmptyState title="No stores to show" text={onlyMissing ? 'Every store has a goal.' : 'Change the search or filters.'} /> : narrow ? shown.map((r) => (
                <div key={r.store.id} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.line, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <div><div style={{ fontWeight: 700, color: COLORS.navy }}>{r.store.name}</div><div style={{ fontSize: '0.72rem', color: COLORS.slate }}>{r.store.route} · {r.store.rep}</div></div>
                        {input(r)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.slate, marginTop: 6 }}>Prev. goal {r.prevGoal === null ? '—' : money(r.prevGoal)} · Prev. sales {money(r.prevSales)} · {change(r)}</div>
                </div>
            )) : (
                <div className="tableFixHeadSmall">
                    <table className="table table-clean">
                        <thead><tr><th>Route</th><th>Store</th><th>Rep</th><th>Prev. sales</th><th>Prev. goal</th><th>Goal</th><th>Change</th></tr></thead>
                        <tbody>
                            {shown.map((r) => (
                                <tr key={r.store.id}>
                                    <td>{r.store.route}</td>
                                    <td><div style={{ fontWeight: 600 }}>{r.store.name}</div><div style={{ fontSize: '0.72rem', color: COLORS.slate }}>{r.store.city}</div></td>
                                    <td>{r.store.rep}</td>
                                    <td>{money(r.prevSales)}</td>
                                    <td>{r.prevGoal === null ? '—' : money(r.prevGoal)}</td>
                                    <td>{input(r)}</td>
                                    <td>{r.goal === null ? <Chip tone="amber">Missing</Chip> : change(r)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="tfoot"><tr><td colSpan={3}>{shown.length} stores shown</td><td></td><td>{money(prevTotal)}</td><td>{money(total)}</td><td></td></tr></tfoot>
                    </table>
                </div>
            )}

            {modal === 'percent' ? <PercentModal ctx={ctx} month={month} prev={prev} all={all} onClose={() => setModal(null)} /> : null}
            {modal === 'paste' ? <PasteModal ctx={ctx} month={month} stores={sales.stores} onClose={() => setModal(null)} /> : null}
            <Drawer show={history} title="Change history" subtitle={monthLabelOf(month)} onClose={() => setHistory(false)}>
                {sales.goalHistory.filter((h) => h.month === month).length === 0 ? <EmptyState title="No changes yet" text="Edits, bulk updates and approvals show up here." /> : sales.goalHistory.filter((h) => h.month === month).map((h) => {
                    const st = h.storeId ? sales.stores.filter((s) => s.id === h.storeId)[0] : null
                    return (
                        <div key={h.id} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.line, borderRadius: 10, padding: '10px 12px', marginBottom: 8, fontSize: '0.83rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <strong>{h.kind === 'goal' ? (st ? st.name : 'Goal') : h.kind === 'bulk' ? 'Bulk update' : 'Status change'}</strong>
                                <span style={{ color: COLORS.muted, fontSize: '0.72rem' }}>{fmtStamp(h.at)}</span>
                            </div>
                            <div style={{ color: COLORS.slate }}>
                                {h.kind === 'goal' ? (h.from === null ? 'Not set' : money(h.from)) + ' → ' + (h.to === null ? 'Removed' : money(h.to)) : h.kind === 'bulk' ? h.to + ' stores · ' + h.note : (STATUS_LABEL[h.from] || h.from) + ' → ' + (STATUS_LABEL[h.to] || h.to) + (h.note ? ' · ' + h.note : '')}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: COLORS.muted }}>by {h.by}</div>
                        </div>
                    )
                })}
            </Drawer>
        </React.Fragment>
    )
}

function PercentModal({ ctx, month, prev, all, onClose }) {
    const [value, setValue] = useState('5')
    const [base, setBase] = useState('prev')
    const [busy, run] = useSaving()
    const n = Number(value)
    const bad = value === '' || isNaN(n) || n < -90 || n > 200
    const apply = () => {
        if (bad) return
        const entries = {}
        all.forEach((r) => {
            const from = base === 'prev' ? r.prevGoal : (r.goal !== null ? r.goal : r.prevGoal)
            if (from !== null) entries[r.store.id] = Math.round(from * (1 + n / 100) / 250) * 250
        })
        run(() => { ctx.dispatch({ type: 'GOALS_BULK', month: month, entries: entries, by: ctx.me.name, note: (n >= 0 ? '+' : '') + n + '% on ' + (base === 'prev' ? monthLabelOf(prev) + ' goals' : 'current goals') }); ctx.toast('Applied ' + n + '% to ' + Object.keys(entries).length + ' stores.'); onClose() })
    }
    return (
        <DashModalV2 show onClose={onClose} title="Apply a % change" subtitle={'Goals for ' + monthLabelOf(month)} size="md"
            footer={<React.Fragment><Btn kind="secondary" onClick={onClose}>Cancel</Btn><Btn busy={busy} onClick={apply} disabled={bad}>Apply</Btn></React.Fragment>}>
            <Field label="Change (%)" error={bad ? 'Enter a number between -90 and 200' : ''} hint="Results are rounded to the nearest $250.">
                <input type="number" value={value} onChange={(e) => setValue(e.target.value)} style={{ ...INPUT_STYLE, width: 120 }} />
            </Field>
            <Field label="Apply on">
                <label style={{ display: 'flex', gap: 8, marginBottom: 6 }}><input type="radio" checked={base === 'prev'} onChange={() => setBase('prev')} /> {monthLabelOf(prev)} goals (all stores)</label>
                <label style={{ display: 'flex', gap: 8 }}><input type="radio" checked={base === 'cur'} onChange={() => setBase('cur')} /> Current goals (fills the empty ones from {monthLabelOf(prev).split(' ')[0]})</label>
            </Field>
        </DashModalV2>
    )
}

function PasteModal({ ctx, month, stores, onClose }) {
    const [text, setText] = useState('')
    const [busy, run] = useSaving()
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
    const parsed = lines.map((l) => {
        const parts = l.split(/\t|;|,(?=\s*\$?[\d.]+\s*$)/)
        const name = (parts[0] || '').trim().toLowerCase()
        const amount = Number((parts[parts.length - 1] || '').replace(/[$,\s]/g, ''))
        const store = stores.filter((s) => s.name.toLowerCase() === name || s.id === name)[0]
        return { raw: l, store: store, amount: amount, ok: !!store && !isNaN(amount) && amount >= 0 && parts.length > 1 }
    })
    const good = parsed.filter((p) => p.ok)
    const apply = () => {
        const entries = {}
        good.forEach((p) => { entries[p.store.id] = Math.round(p.amount) })
        run(() => { ctx.dispatch({ type: 'GOALS_BULK', month: month, entries: entries, by: ctx.me.name, note: 'Pasted from Excel' }); ctx.toast(good.length + ' goals imported' + (parsed.length - good.length ? ', ' + (parsed.length - good.length) + ' rows skipped.' : '.')); onClose() })
    }
    const sample = stores.slice(0, 3).map((s) => s.name + '\t' + (4000 + Math.round(Math.random() * 4) * 250)).join('\n')
    return (
        <DashModalV2 show onClose={onClose} title="Paste goals from Excel" subtitle="One store per line: store name, then the amount (tab, semicolon or comma separated)" size="lg"
            footer={<React.Fragment><Btn kind="secondary" onClick={() => setText(sample)}>Use an example</Btn><div style={{ flex: 1 }}></div><Btn kind="secondary" onClick={onClose}>Cancel</Btn><Btn busy={busy} disabled={good.length === 0} onClick={apply}>Import {good.length} goals</Btn></React.Fragment>}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={'Store name\t5000\nAnother store\t3250'} style={{ ...INPUT_STYLE, width: '100%', minHeight: 140, fontFamily: 'monospace' }} />
            {parsed.length > 0 ? (
                <div style={{ marginTop: 10, fontSize: '0.8rem' }}>
                    <Chip tone="green">{good.length} ready</Chip> {parsed.length - good.length > 0 ? <Chip tone="red">{parsed.length - good.length} cannot be matched</Chip> : null}
                    {parsed.filter((p) => !p.ok).slice(0, 5).map((p, i) => <div key={i} style={{ color: COLORS.red, marginTop: 4 }}>Skipped: “{p.raw}”</div>)}
                </div>
            ) : null}
        </DashModalV2>
    )
}
